/**
 * Daily Moves → how-to guide mapping (gap row 49, I8).
 *
 * Every move type returned by the Daily Moves API (`GET /api/v1/ai/moves`, I2:
 * `move.kind`, plus `move.signal` = GTM lane for `plan_item`) links to a
 * section of the bilingual guides on document.karexia.com with a STABLE anchor.
 *
 * Source of truth: `docs-site/guides-src/daily-moves.json` (generated into
 * `docs-site/dist/guides/daily-moves.json`). `moveGuides.test.ts` fails if this
 * table drifts from the generated mapping, so the app never links to a missing
 * anchor.
 */

export const GUIDES_BASE_URL = 'https://document.karexia.com/guides';

export interface MoveGuideEntry {
  kind: string;
  signal: string | null;
  anchor: string;
  route: string;
}

export const MOVE_GUIDES: readonly MoveGuideEntry[] = [
  { kind: 'create_brief', signal: null, anchor: 'move-create-brief', route: '/strategy-mktg/brief' },
  { kind: 'trust_gap', signal: null, anchor: 'move-trust-gap', route: '/strategy-mktg/brief' },
  { kind: 'generate_plan', signal: null, anchor: 'move-generate-plan', route: '/strategy-mktg/brief#seed' },
  { kind: 'plan_item', signal: 'content', anchor: 'move-plan-item-content', route: '/publishing' },
  { kind: 'plan_item', signal: 'community', anchor: 'move-plan-item-community', route: '/listening' },
  { kind: 'plan_item', signal: 'email', anchor: 'move-plan-item-email', route: '/outreach/sequences' },
  { kind: 'plan_item', signal: 'ads', anchor: 'move-plan-item-ads', route: '/ads' },
  { kind: 'plan_item', signal: 'prospecting', anchor: 'move-plan-item-prospecting', route: '/prospection/offres' },
  { kind: 'plan_item', signal: 'bio', anchor: 'move-plan-item-bio', route: '/bio' },
  { kind: 'goal_pacing', signal: null, anchor: 'move-goal-pacing', route: '/strategy-mktg' },
  { kind: 'weekly_checkin', signal: null, anchor: 'move-weekly-checkin', route: '/strategy-mktg' },
  { kind: 'autonomy_promotion', signal: null, anchor: 'move-autonomy-promotion', route: '/strategy-mktg' },
  { kind: 'approvals', signal: null, anchor: 'move-approvals', route: '/inbox/approvals' },
  { kind: 'advisor', signal: null, anchor: 'move-advisor', route: '/recommandations' },
  { kind: 'catch_reply', signal: null, anchor: 'move-catch-reply', route: '/listening/catches' },
  { kind: 'health_pause', signal: null, anchor: 'move-health-pause', route: '/communities/health' },
  { kind: 'audit_fix', signal: null, anchor: 'move-audit-fix', route: '/analytics/site-audit' },
  { kind: 'launch_step', signal: null, anchor: 'move-launch-step', route: '/strategy-mktg/launch-kit?tab=launch-day' },
  { kind: 'content_idea', signal: null, anchor: 'move-content-idea', route: '/listening/catches?tab=problems' },
  // Autopilot hand-off moves (signal = approve / credits / the plan step kind) share one guide.
  { kind: 'autopilot', signal: null, anchor: 'move-autopilot', route: '/autopilot' },
];

/** Locale-aware guide URL for a move, or null when the kind has no guide. */
export function guideUrlFor(
  kind: string,
  signal: string | null | undefined,
  locale: string,
): string | null {
  const lang = locale.toLowerCase().startsWith('en') ? 'en' : 'fr';
  const entry =
    MOVE_GUIDES.find((g) => g.kind === kind && g.signal === (signal ?? null)) ??
    // plan_item with an unknown/absent lane falls back to the content guide
    (kind === 'plan_item' ? MOVE_GUIDES.find((g) => g.anchor === 'move-plan-item-content') : undefined) ??
    // other kinds carry a network / fix category / runbook step as `signal`
    MOVE_GUIDES.find((g) => g.kind === kind && g.signal === null);
  return entry ? `${GUIDES_BASE_URL}/${lang}/daily-moves.html#${entry.anchor}` : null;
}
